import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { PageLoader } from 'routes/PrivateRouter';
import { cashURLs } from 'routes/cash/cashURLs';

import { PermissionGuard } from 'components/PermissionGuard';

import { featureFlags } from 'utils/config/featureFlags';

const { FF__CASH_DASHBOARD, FF__CASH_REPORTING, FF__CASH_POLICIES } =
  featureFlags;

const Cash = lazy(() =>
  import('pages/cash/Cash').then((m) => ({ default: m.Cash }))
);

const CashOverviewElement = () => (
  <PermissionGuard moduleKey="cash">
    <Suspense fallback={<PageLoader />}>
      <Cash section="overview" />
    </Suspense>
  </PermissionGuard>
);

const CashReconciliationElement = () => (
  <PermissionGuard moduleKey="cash">
    <Suspense fallback={<PageLoader />}>
      <Cash section="operations" />
    </Suspense>
  </PermissionGuard>
);

const CashReportingElement = () => (
  <PermissionGuard moduleKey="cash">
    <Suspense fallback={<PageLoader />}>
      <Cash section="reporting" />
    </Suspense>
  </PermissionGuard>
);

const CashPolicies = lazy(() => import('pages/cash/demo/context-graphs/index'));

const CashPoliciesElement = () => (
  <PermissionGuard moduleKey="cash">
    <Suspense fallback={<PageLoader />}>
      <CashPolicies />
    </Suspense>
  </PermissionGuard>
);

export const CashRoutes = {
  path: cashURLs._base_relative_path,
  children: [
    // Base redirect
    {
      index: true,
      element: (
        <Navigate
          to={FF__CASH_DASHBOARD ? cashURLs.overview : cashURLs.reconciliation}
          replace
        />
      ),
    },

    // Overview section
    ...(FF__CASH_DASHBOARD
      ? [
          {
            path: cashURLs._overview_relative_path,
            element: <CashOverviewElement />,
          },
        ]
      : []),

    // Reconciliation section with nested menus
    {
      path: cashURLs._reconciliation_relative_path,
      children: [
        {
          index: true,
          element: <Navigate to={cashURLs.reconciliation_for_review} replace />,
        },
        {
          path: cashURLs._pending_relative_path,
          element: <CashReconciliationElement />,
        },
        {
          path: cashURLs._for_review_relative_path,
          element: (
            <Navigate
              to={cashURLs.reconciliation_for_review_reconcile}
              replace
            />
          ),
        },
        {
          path: `${cashURLs._for_review_relative_path}/:tab`,
          element: <CashReconciliationElement />,
        },
        {
          path: cashURLs._recorded_relative_path,
          element: <CashReconciliationElement />,
        },
        // Journal Entries route (behind feature flag)
        {
          path: cashURLs._journal_entries_relative_path,
          element: <CashReconciliationElement />,
        },
        // Reconciled route (behind feature flag)
        {
          path: cashURLs._reconciled_relative_path,
          element: <CashReconciliationElement />,
        },
      ],
    },

    // Reporting section
    ...(FF__CASH_REPORTING
      ? [
          {
            path: cashURLs._reporting_relative_path,
            element: <CashReportingElement />,
          },
        ]
      : []),

    // Policies section with nested routes
    ...(FF__CASH_POLICIES
      ? [
          {
            path: cashURLs._policies_relative_path,
            children: [
              {
                index: true,
                element: <CashPoliciesElement />,
              },
              {
                path: cashURLs._precedents_relative_path,
                element: <CashPoliciesElement />,
              },
              {
                path: ':policyId',
                element: <CashPoliciesElement />,
              },
            ],
          },
        ]
      : []),
  ],
};
